// Stephanet's pay15/shared.jsx and paysys/shared.jsx: formatting, the KPI tile, panel and chip, the screen header, and the month's data
// (meta, the selected payroll month and every engine row), cached between screens like Stephanet's.
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Download, Printer, RefreshCw, Search } from 'lucide-react';
import Modal from '../hr/components/Modal';
import Loader from '../hr/components/Loader';
import { DateInput, MonthInput, Select } from '../hr/components/Controls';
import * as api from './api';

const f0 = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 2 });
const f2 = new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const n0 = v => (v === null || v === undefined || v === '' ? '—' : f0.format(Number(v)));
export const n2 = v => (v === null || v === undefined || v === '' ? '—' : f2.format(Number(v)));
export const naira = v => `₦${f2.format(Number(v || 0))}`;
export const nairaK = v => (Math.abs(v) >= 1e6 ? `₦${(v / 1e6).toFixed(1)}M` : `₦${Math.round(v / 1000).toLocaleString()}k`);
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const PALETTE = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0d9488', '#ea580c', '#db2777', '#0284c7', '#65a30d'];
export const plabel = p => { if (!p) return ''; const [y, m] = p.split('-'); return `${MONTHS[Number(m) - 1]} ${y}`; };

export function Kpi({ label, value, note, tone = 'blue', Ico, i = 0 }) {
  return (
    <div className={`ps-kpi t-${tone}`} style={{ animationDelay: `${i * 30}ms` }}>
      <div className="ps-kpi-h"><span>{label}</span>{Ico && <Ico size={16} />}</div>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}
export function Chip({ tone = 'gray', children }) { return <span className={`mdr-chip t-${tone}`}>{children}</span>; }
export function Panel({ title, note, children, className = '' }) {
  return <div className={`mdr-card ps-panel ${className}`}><div className="ps-panel-h"><h3>{title}</h3>{note && <span>{note}</span>}</div>{children}</div>;
}
export function PyHeader({ title, sub, meta, period, children }) {
  return (
    <div className="mdr-top ps-top py-top">
      <div>
        <h2>{title}</h2>
        {sub && <p>{sub}</p>}
        {period && <span className="py-period-tag">Payroll month: <b>{plabel(period)}</b>{period === meta?.period ? ' · active' : ''}</span>}
      </div>
      {children && <div className="ps-top-actions no-print">{children}</div>}
    </div>
  );
}
// the app's RNL loading spinner
export const Loading = ({ what = 'payroll' }) => <Loader title={`Loading ${what}…`} subtitle="Fetching payroll records" />;
export const ErrorBox = ({ error }) => <div className="error">{error}</div>;

let metaCache = null;
let periodSel = null;
const dataCache = {};
const listeners = new Set();

export function usePyMeta() {
  const [meta, setMeta] = useState(metaCache);
  const [err, setErr] = useState('');
  const reload = () => api.meta().then(m => { metaCache = m; setMeta(m); setErr(''); return m; }).catch(e => { setErr(e.message || 'Could not load the payroll month'); });
  useEffect(() => { if (!metaCache) reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [meta, reload, err];
}
export function usePyPeriod(meta) {
  const [p, setP] = useState(periodSel || '');
  useEffect(() => { if (!p && meta) setP(periodSel || meta.period); }, [meta]); // eslint-disable-line react-hooks/exhaustive-deps
  return [p, v => { periodSel = v; setP(v); }];
}
// the month's payroll (every engine row); invalidate() after any change, every open screen reloads
export function usePyData(period) {
  const [d, setD] = useState(period ? dataCache[period] : null);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => { const fn = () => setTick(t => t + 1); listeners.add(fn); return () => listeners.delete(fn); }, []);
  useEffect(() => {
    if (!period) return;
    if (dataCache[period]) { setD(dataCache[period]); return; }
    setD(null);
    api.monthData(period).then(x => { dataCache[period] = x; setD(x); setErr(''); }).catch(e => setErr(e.message));
  }, [period, tick]);
  return [d, err, () => { delete dataCache[period]; setTick(t => t + 1); }];
}
export function invalidate() { Object.keys(dataCache).forEach(k => delete dataCache[k]); metaCache = null; listeners.forEach(fn => fn()); }

export const pctx = v => (v === null || v === undefined || v === '' ? '—' : `${(Number(v) * 100).toLocaleString('en-NG', { maximumFractionDigits: 2 })}%`);
export const dmy = v => { if (!v) return '—'; const d = new Date(`${String(v).slice(0, 10)}T00:00:00`); return isNaN(d) ? v : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };

export const clearPy = invalidate;
export const can = (meta, screen, action = 'edit') => !!meta?.rights?.[screen]?.[action];

export function Sel({ label, value, onChange, options, all = 'All' }) {
  return (
    <label className="ps-field"><span>{label}</span>
      <Select title={label} value={value} onChange={e => onChange(e.target.value)}>
        {all !== null && <option value="">{all}</option>}
        {options.map(o => (typeof o === 'object' ? <option key={o.value} value={o.value}>{o.label}</option> : <option key={o} value={o}>{o}</option>))}
      </Select>
    </label>
  );
}
export function SearchBox({ value, onChange, placeholder = 'Search employee, number, grade…' }) {
  return (
    <label className="ps-field ps-search"><span>Search</span>
      <div><Search size={14} /><input value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} /></div>
    </label>
  );
}

// fields: {k, label, type: text|number|money|pct|date|month|select|check|textarea, options, req, hint, span, disabled}
export function EditModal({ title, fields, value, onSave, onClose, wide = true, children, saveLabel = 'Save', note }) {
  const [f, setF] = useState(() => ({ ...value }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    const miss = fields.filter(x => x.req && (f[x.k] === undefined || f[x.k] === null || String(f[x.k]).trim() === ''));
    if (miss.length) { setErr(`Please fill: ${miss.map(x => x.label).join(', ')}`); return; }
    setBusy(true); setErr('');
    try { await onSave(f); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} wide={wide} className="rec-modal py-modal">
      {note && <p className="muted small py-note">{note}</p>}
      <div className="rec-form-grid">
        {fields.map(x => <Field key={x.k} x={x} v={f[x.k]} set={v => set(x.k, v)} f={f} />)}
      </div>
      {children && children(f, set)}
      {err && <div className="error py-err">{err}</div>}
      <div className="mdh-form-foot rec-foot">
        <button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className="mds-run" disabled={busy} onClick={save}>{busy ? 'Saving…' : saveLabel}</button>
      </div>
    </Modal>
  );
}

function Field({ x, v, set, f }) {
  if (x.show && !x.show(f)) return null;
  const cls = `rec-f ${x.span === 2 ? 's2' : ''}`;
  const lab = <span>{x.label}{x.req ? ' *' : ''}</span>;
  if (x.type === 'check') return <label className="rec-check"><input type="checkbox" checked={!!v} disabled={x.disabled} onChange={e => set(e.target.checked)} /> {x.label}</label>;
  if (x.type === 'select') {
    const opts = typeof x.options === 'function' ? x.options(f) : x.options;
    return <label className={cls}>{lab}<Select title={x.label} value={v ?? ''} disabled={x.disabled} onChange={e => set(e.target.value)}>
      {x.blank !== false && <option value="">{x.blank || '—'}</option>}
      {opts.map(o => (typeof o === 'object' ? <option key={o.value} value={o.value}>{o.label}</option> : <option key={o}>{o}</option>))}
    </Select>{x.hint && <small className="muted">{x.hint}</small>}</label>;
  }
  if (x.type === 'textarea') return <label className="rec-f s2">{lab}<textarea rows={3} value={v ?? ''} onChange={e => set(e.target.value)} /></label>;
  if (x.type === 'date') return <label className={cls}>{lab}<DateInput value={v ? String(v).slice(0, 10) : ''} disabled={x.disabled} onChange={e => set(e.target.value)} />{x.hint && <small className="muted">{x.hint}</small>}</label>;
  if (x.type === 'month') return <label className={cls}>{lab}<MonthInput value={v ? String(v).slice(0, 7) : ''} disabled={x.disabled} onChange={e => set(e.target.value)} />{x.hint && <small className="muted">{x.hint}</small>}</label>;
  const type = { number: 'number', money: 'number', pct: 'number' }[x.type] || 'text';
  const shown = x.type === 'pct' ? (v === '' || v === null || v === undefined ? '' : +(Number(v) * 100).toFixed(4)) : v ?? '';
  return <label className={cls}>{lab}<input type={type} step={x.step || (x.type === 'money' ? '0.01' : 'any')} value={shown} disabled={x.disabled} placeholder={x.ph}
    onChange={e => { const t = e.target.value; set(x.type === 'pct' ? (t === '' ? '' : Number(t) / 100) : (type === 'number' ? (t === '' ? '' : Number(t)) : t)); }} />
    {x.hint && <small className="muted">{x.hint}</small>}</label>;
}

export function Confirm({ title, text, onYes, onClose, yes = 'Confirm', danger }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose}>
      <p>{text}</p>
      <div className="mdh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className={danger ? 'mdr-btn danger' : 'mds-run'} disabled={busy} onClick={async () => { setBusy(true); try { await onYes(); } finally { setBusy(false); } }}>{yes}</button></div>
    </Modal>
  );
}

export function Stat({ label, value, tone }) { return <div className={`py-stat ${tone ? `t-${tone}` : ''}`}><small>{label}</small><b>{value}</b></div>; }

export function Tabs({ tabs, value, onChange }) {
  return <div className="py-tabs no-print">{tabs.map(([k, l]) => <button key={k} className={value === k ? 'on' : ''} onClick={() => onChange(k)}>{l}</button>)}</div>;
}
export const empOptions = rows => rows.map(r => ({ value: String(r.id), label: `${r.employee_no} — ${r.name}` }));

// ── Stephanet's workbook table, CSV and print (custom/paysys/shared.jsx) ──
export const pct = v => `${f0.format(Number(v || 0))}%`;
export const TYPE_TONE = { BASIC: 'blue', DAILY: 'teal', FIXED: 'purple', OTHERS: 'gray' };
export const words = q => (q || '').toLowerCase().split(/\s+/).filter(Boolean);
export const matches = (r, q, keys) => { const w = words(q); if (!w.length) return true; const t = keys.map(k => r[k] ?? '').join(' ').toLowerCase(); return w.every(x => t.includes(x)); };
export const sum = (rows, f) => rows.reduce((t, r) => t + (Number(typeof f === 'function' ? f(r) : r[f]) || 0), 0);

export function downloadCsv(filename, head, rows) {
  const q = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [head.map(q).join(','), ...rows.map(r => r.map(q).join(','))].join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  a.download = filename; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 500);
}
export const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
export function printHtml(title, body, landscape = true) {
  const w = window.open('', '_blank', 'width=1100,height=900');
  if (!w) return false;
  w.document.write(`<html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
    @page{size:${landscape ? 'A4 landscape' : 'A4'};margin:10mm}body{font-family:Arial,sans-serif;color:#0f172a;margin:0;font-size:10px}
    h1{font-size:16px;margin:0 0 2px}p.s{margin:0 0 10px;color:#475569}table{width:100%;border-collapse:collapse}th,td{border:1px solid #cbd5e1;padding:3px 4px;white-space:nowrap}
    th{background:#0b318b;color:#fff;text-align:left;font-size:9px}td.r,th.r{text-align:right}tfoot td{font-weight:700;background:#eef2f9}
    .slip{page-break-after:always;border:1px solid #cbd5e1;padding:14px;margin-bottom:14px}.slip:last-child{page-break-after:auto}
    .sh{display:flex;justify-content:space-between;border-bottom:3px solid #0b318b;padding-bottom:6px;margin-bottom:8px}
    .kv{display:grid;grid-template-columns:repeat(4,1fr);gap:4px;margin-bottom:8px}.kv div{border:1px solid #e2e8f0;padding:4px}.kv small{display:block;color:#64748b;font-size:8px;text-transform:uppercase}
    .two{display:grid;grid-template-columns:1fr 1fr;gap:10px}.net{margin-top:8px;padding:8px;background:#0b318b;color:#fff;font-size:14px;font-weight:700;display:flex;justify-content:space-between}
    </style></head><body>${body}<script>window.onload=()=>{window.print()}</script></body></html>`);
  w.document.close();
  return true;
}

const cell = (c, r) => {
  const v = typeof c.get === 'function' ? c.get(r) : r[c.key];
  if (c.kind === 'money') return n2(v);
  if (c.kind === 'num') return n0(v);
  if (c.kind === 'pct') return v === null || v === undefined ? '—' : pct(v);
  if (c.kind === 'type') return <Chip tone={TYPE_TONE[v]}>{v}</Chip>;
  return v === null || v === undefined || v === '' ? '—' : v;
};
export const cellText = (c, r) => { const v = typeof c.get === 'function' ? c.get(r) : r[c.key]; return v === null || v === undefined ? '' : v; };
const isNum = c => ['money', 'num', 'pct'].includes(c.kind);
const left = (lead, cols, i) => ({ left: lead * 32 + 44 + cols.slice(0, i).reduce((t, x) => t + (x.w || 120), 0), minWidth: cols[i].w || 120, maxWidth: cols[i].w || 120 });

export function Workbook({ cols, rows, frozen = 2, totals = true, onRow, selected, onSelect, empty = 'No employees match the filters.', maxHeight = 560, rowClass }) {
  const [sort, setSort] = useState(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = cols.find(x => x.key === sort.key);
    const get = r => cellText(c, r);
    return [...rows].sort((a, b) => {
      const x = get(a); const y = get(b);
      const d = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
      return sort.dir * d;
    });
  }, [rows, sort, cols]);
  const sums = useMemo(() => Object.fromEntries(cols.filter(c => c.kind === 'money' || (c.kind === 'num' && c.sum)).map(c => [c.key, rows.reduce((t, r) => t + (Number(cellText(c, r)) || 0), 0)])), [cols, rows]);
  const sel = selected ? new Set(selected) : null;
  const allOn = sel && rows.length > 0 && rows.every(r => sel.has(r.id));
  const lead = sel ? 1 : 0;
  return (
    <div className="ps-wb" style={{ maxHeight }}>
      <table className="ps-table">
        <thead>
          <tr>
            {sel && <th className="fz ps-ck" style={{ left: 0 }}><input type="checkbox" checked={allOn} onChange={() => onSelect(allOn ? [] : rows.map(r => r.id))} /></th>}
            <th className="fz ps-sn" style={{ left: lead * 32 }}>S/N</th>
            {cols.map((c, i) => (
              <th key={c.key} className={`${i < frozen ? 'fz' : ''} ${isNum(c) ? 'r' : ''} ${c.group ? `g-${c.group}` : ''}`}
                  style={i < frozen ? left(lead, cols, i) : { minWidth: c.w }}
                  onClick={() => setSort(x => (x?.key === c.key ? (x.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}>
                {c.label}{sort?.key === c.key && (sort.dir === 1 ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, n) => (
            <tr key={r.id ?? n} className={`${onRow ? 'clickable' : ''} ${sel?.has(r.id) ? 'on' : ''} ${rowClass ? rowClass(r) : ''}`} onClick={onRow ? () => onRow(r) : undefined}>
              {sel && <td className="fz ps-ck" style={{ left: 0 }} onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={sel.has(r.id)} onChange={() => onSelect(sel.has(r.id) ? selected.filter(x => x !== r.id) : [...selected, r.id])} /></td>}
              <td className="fz ps-sn" style={{ left: lead * 32 }}>{n + 1}</td>
              {cols.map((c, i) => (
                <td key={c.key} className={`${i < frozen ? 'fz' : ''} ${isNum(c) ? 'r mono' : ''} ${c.cls || ''}`}
                    style={i < frozen ? left(lead, cols, i) : undefined} title={i < frozen ? String(cellText(c, r)) : undefined}>{cell(c, r)}</td>
              ))}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length + 1 + lead} className="ps-empty">{empty}</td></tr>}
        </tbody>
        {totals && rows.length > 0 && <tfoot>
          <tr>
            {sel && <td className="fz ps-ck" style={{ left: 0 }} />}
            <td className="fz ps-sn" style={{ left: lead * 32 }} />
            {cols.map((c, i) => (
              <td key={c.key} className={`${i < frozen ? 'fz' : ''} ${isNum(c) ? 'r mono' : ''}`} style={i < frozen ? left(lead, cols, i) : undefined}>
                {i === 0 ? `TOTAL (${rows.length})` : c.key in sums ? (c.kind === 'money' ? n2(sums[c.key]) : n0(sums[c.key])) : ''}
              </td>
            ))}
          </tr>
        </tfoot>}
      </table>
    </div>
  );
}

export function exportRows(filename, cols, rows) {
  downloadCsv(filename, ['S/N', ...cols.map(c => c.label)], rows.map((r, i) => [i + 1, ...cols.map(c => cellText(c, r))]));
}
export function printRows(title, subtitle, cols, rows) {
  const head = `<tr><th>S/N</th>${cols.map(c => `<th class="${isNum(c) ? 'r' : ''}">${esc(c.label)}</th>`).join('')}</tr>`;
  const fmt = (c, v) => (c.kind === 'money' ? n2(v) : c.kind === 'num' ? n0(v) : c.kind === 'pct' ? pct(v) : v ?? '');
  const body = rows.map((r, i) => `<tr><td>${i + 1}</td>${cols.map(c => `<td class="${isNum(c) ? 'r' : ''}">${esc(fmt(c, cellText(c, r)))}</td>`).join('')}</tr>`).join('');
  const foot = `<tr><td></td>${cols.map((c, i) => `<td class="r">${i === 0 ? 'TOTAL' : c.kind === 'money' ? n2(rows.reduce((t, r) => t + (Number(cellText(c, r)) || 0), 0)) : ''}</td>`).join('')}</tr>`;
  return printHtml(title, `<h1>${esc(title)}</h1><p class="s">${esc(subtitle)}</p><table><thead>${head}</thead><tbody>${body}</tbody><tfoot>${foot}</tfoot></table>`);
}

export function Toolbar({ onRefresh, onExport, onPrint, children }) {
  return <>
    {children}
    {onRefresh && <button className="mdr-btn" onClick={onRefresh}><RefreshCw size={14} /> Refresh</button>}
    {onExport && <button className="mdr-btn" onClick={onExport}><Download size={14} /> Export</button>}
    {onPrint && <button className="mdr-btn" onClick={onPrint}><Printer size={14} /> Print</button>}
  </>;
}

export function PeriodPick({ meta, value, onChange, label = 'Payroll Month' }) {
  const opts = useMemo(() => {
    const st = new Set(meta?.periods || []);
    if (meta?.period) {
      const [y, m] = meta.period.split('-').map(Number);
      for (let i = -3; i <= 12; i++) { const d = new Date(y, m - 1 - i, 1); st.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); }
    }
    return [...st].sort().reverse();
  }, [meta]);
  return (
    <label className="ps-field"><span>{label}</span>
      <Select title={label} value={value} onChange={e => onChange(e.target.value)}>
        {opts.map(p => <option key={p} value={p}>{plabel(p)}{p === meta?.period ? ' (active)' : ''}</option>)}
      </Select>
    </label>
  );
}

export function Filters({ meta, period, setPeriod, f, setF, show = ['structure', 'site', 'q'], children }) {
  return (
    <div className="mdr-card ps-filters no-print">
      {setPeriod && <PeriodPick meta={meta} value={period} onChange={setPeriod} />}
      {show.includes('structure') && <Sel label="Pay Structure" value={f.structure} onChange={v => setF({ ...f, structure: v })} options={['NET', 'GROSS', 'DAILY', 'FIXED']} all="All Structures" />}
      {show.includes('site') && <Sel label="Home Site" value={f.site} onChange={v => setF({ ...f, site: v })} options={(meta?.sites || []).map(x => x.name)} all="All Sites" />}
      {show.includes('dept') && <Sel label="Department" value={f.dept} onChange={v => setF({ ...f, dept: v })} options={(meta?.departments || []).map(x => x.name)} all="All Departments" />}
      {show.includes('payer') && <Sel label="Who Pays" value={f.payer} onChange={v => setF({ ...f, payer: v })} options={(meta?.payers || []).map(x => x.name)} all="All" />}
      {show.includes('q') && <SearchBox value={f.q} onChange={v => setF({ ...f, q: v })} />}
      {children}
    </div>
  );
}
export const applyFilters = (rows, f) => rows.filter(r => (!f.structure || r.structure === f.structure) && (!f.site || r.site === f.site) &&
  (!f.dept || r.department === f.dept) && (!f.payer || r.who_pays === f.payer) && matches(r, f.q, ['employee_no', 'name', 'grade', 'site', 'department', 'job_title']));

// the standard engine columns used by the registers (label, field, kind)
export const COL = {
  employee_no: { key: 'employee_no', label: 'Employee ID', w: 96 }, name: { key: 'name', label: 'Name', w: 170 },
  structure: { key: 'structure', label: 'Pay structure', w: 100 }, grade: { key: 'grade', label: 'Grade', w: 70 },
  site: { key: 'site', label: 'Home site', w: 170 }, department: { key: 'department', label: 'Department', w: 150 }, who_pays: { key: 'who_pays', label: 'Who pays', w: 100 },
};
export const m = (key, label, extra = {}) => ({ key, label, kind: 'money', w: 120, ...extra });
export const q = (key, label, extra = {}) => ({ key, label, kind: 'num', sum: true, w: 90, ...extra });
export const STRUCT_TONE = { NET: 'purple', GROSS: 'blue', DAILY: 'teal', FIXED: 'gray' };
export const StructChip = ({ v }) => (v ? <Chip tone={STRUCT_TONE[v] || 'gray'}>{v}</Chip> : '—');
